import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  FormGroup,
  FormControl,
  Container,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import US_STATES from "../../constants/states";
import COUNTIES from "../Counties";
import axios from "axios";
import "./EditDocumentModal.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisVertical, faInfoCircle } from "@fortawesome/free-solid-svg-icons";

const categoryRequirements = {
  general: ["expirationDate"],
  state: ["expirationDate", "state"],
  county: ["expirationDate", "state", "county"],
  property: ["expirationDate", "state", "county", "propertyId"],
  SB156: ["expirationDate"],
};

const EditDocumentModal = ({ show, onHide, document, onSave, setShowToast, setToastInfo }) => {
  const [doc, setDoc] = useState({
    name: "",
    docType: "",
    propertyId: "",
    containerName: "",
    expirationDate: new Date(),
    state: "",
    county: "",
    saleDate: new Date(),
    saleId: "",
    category: "",
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? null : date; // Return null if date is invalid
  };

  useEffect(() => {
    if (document && document.metadata) {
      setDoc({
        name: document.name || "",
        docType: document.metadata.doctype || "",
        propertyId: document.metadata.propertyid || "",
        containerName: document.containerName || "Unknown",
        expirationDate: formatDate(document.metadata.expirationdate) || undefined,
        state: document.metadata.state || "",
        county: document.metadata.county || "",
        saleDate: formatDate(document.metadata.saledate) || undefined,
        saleId: document.metadata.saleId || "",
        category: document.metadata.category || "",
      });
    }
  }, [document]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setDoc((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_SERVER_URL;
      const payload = {
        metadata: {
          docType: doc.docType,
          propertyId: doc.propertyId,
          expirationDate: doc.expirationDate.toISOString().split("T")[0],
          state: doc.state,
          county: doc.county,
          saleDate: doc.saleDate.toISOString().split("T")[0],
          saleId: doc.saleId,
        },
      };

      await axios.put(`${API_BASE_URL}/blob/container/${doc.containerName}/update/${doc.name}`, payload);
      setShowToast(true);
      setToastInfo({
        header: "Success!",
        body: "Successfully Edited Document",
        variant: "warning"
      });
      onSave(); // Refresh the parent component
      onHide(); // Close the modal
    } catch (error) {
      console.error("Error updating document:", error);
      setShowToast(true);
      setToastInfo({
        header: "Error",
        body: "Something went wrong.",
        variant: "danger"
      });
    }
  };

  //   const handleDelete = async () => {
  //     const yesterday = new Date();
  //     yesterday.setDate(yesterday.getDate() - 1);
  //     try {
  //       const API_BASE_URL = process.env.REACT_APP_SERVER_URL;
  //       const API_KEY = process.env.REACT_APP_SERVER_API_KEY;
  //       await axios.put(`${API_BASE_URL}/blob/container/${doc.containerName}/update/${doc.name}`, {
  //         metadata: {
  //           ...doc,
  //           expirationDate: yesterday.toISOString().split("T")[0], // Set expiration date to yesterday
  //         },
  //         headers: {
  //           "Content-Type": "multipart/form-data",
  //           "x-api-key": API_KEY,
  //         },
  //       });
  //       onSave(); // Refresh after "deletion"
  //       onHide(); // Close the modal
  //     } catch (error) {
  //       console.error('Error "deleting" document by setting expiration:', error);
  //     }
  //   };

  const isRequired = (inputName) => {
    if (!document.category) {
      return false;
    }
    const requirements = categoryRequirements[document.category.toLocaleLowerCase()] || [];
    return requirements.includes(inputName) ? true : false;
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton></Modal.Header>
      <Modal.Body>
        <div className="documentModal">
          <div
            style={{
              backgroundColor: "#FF6D40",
              color: "white",
              textAlign: "center",
              fontWeight: "bold",
              padding: "10px 0",
            }}
          >
            <h2>Edit Document</h2>
          </div>
          <div style={{ width: "100%", overflowY: "auto" }}>
            <Container className="m-0">
              <Form>
                <FormGroup className="uploadGroup">
                  <Form.Label>Name</Form.Label>
                  <FormControl type="text" name="name" value={doc.name} onChange={handleInputChange} />
                </FormGroup>
                <Row>
                  <FormGroup className="uploadGroup col-6">
                    <Form.Label>Type</Form.Label>
                    <FormControl as="select" name="docType" value={doc.docType} onChange={handleInputChange}>
                      <option value="">Select Document Type</option>
                      <option value="CountyDocs">County Documents</option>
                      <option value="GenDocs">General Documents</option>
                      <option value="Image">Image</option>
                      <option value="PropDocs">Property Documents</option>
                      <option value="SaleDocs">Sale Documents</option>
                      <option value="sb156">SB156</option>
                      <option value="SRIDocs">SRI Documents</option>
                      <option value="StateDocs">State Documents</option>
                      <option value="TaxDocs">Tax Documents</option>
                    </FormControl>
                  </FormGroup>
                </Row>
                <FormGroup className="uploadGroup">
                  <Form.Label>Property ID</Form.Label>
                  <FormControl type="text" name="propertyId" value={doc.propertyId} onChange={handleInputChange} />
                </FormGroup>
                <Row>
                  <Col md={{ span: 6 }}>
                    <FormGroup className="uploadGroup">
                      <Form.Label>Container</Form.Label>
                      <FormControl
                        type="text"
                        name="containerName"
                        value={doc.containerName}
                        disabled
                        // onChange={handleInputChange}
                        readOnly
                      />
                    </FormGroup>
                  </Col>
                  <Col className="ps-0">
                    <OverlayTrigger
                      delay={{ show: 250, hide: 100 }}
                      placement="right"
                      overlay={
                        <Tooltip id="tooltip-top">
                          To change the container, select the "Move" option from the{" "}
                          <FontAwesomeIcon icon={faEllipsisVertical} /> menu in the document table.
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon icon={faInfoCircle} className="editDocumentIcon" />
                    </OverlayTrigger>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup className="uploadGroup">
                      <Form.Label>Sale Date {isRequired("saleDate") ? "(Required)" : ""}</Form.Label>
                      <DatePicker
                        selected={doc.saleDate}
                        onChange={(date) => setDoc({ ...doc, saleDate: date })}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select sale date"
                        required={isRequired("saleDate")}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup className="uploadGroup">
                      <Form.Label>Expiration Date {isRequired("expirationDate") ? "(Required)" : ""}</Form.Label>
                      <DatePicker
                        selected={doc.expirationDate}
                        onChange={(date) => setDoc({ ...doc, expirationDate: date })}
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        placeholderText="Select expiration date"
                        required={isRequired("expirationDate")}
                        isClearable
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup className="uploadGroup">
                      <Form.Label>State {isRequired("state") ? "(Required)" : ""}</Form.Label>
                      <Form.Control
                        as="select"
                        name="state"
                        value={doc.state}
                        onChange={(e) => setDoc({ ...doc, state: e.target.value, county: "" })}
                        required={isRequired("state")}
                      >
                        <option value="">Select State</option>
                        {US_STATES.map((state) => (
                          <option key={state.code} value={state.code}>
                            {state.name}
                          </option>
                        ))}
                      </Form.Control>
                    </FormGroup>
                  </Col>
                  <Col>
                    <FormGroup className="uploadGroup">
                      <Form.Label>County {isRequired("county") ? "(Required)" : ""}</Form.Label>
                      <Form.Control
                        as="select"
                        name="county"
                        value={doc.county}
                        onChange={(e) => setDoc({ ...doc, county: e.target.value })}
                        required={isRequired("county")}
                      >
                        <option value="">Select County</option>
                        {COUNTIES[doc.state]?.map((county) => (
                          <option key={county.code} value={county.code}>
                            {county.name}
                          </option>
                        ))}
                      </Form.Control>
                    </FormGroup>
                  </Col>
                </Row>
                <Row></Row>
                <FormGroup className="uploadGroup">
                  <Form.Label>Sale ID {isRequired("saleId") ? "(Required)" : ""}</Form.Label>
                  <FormControl
                    type="text"
                    name="saleId"
                    value={doc.saleId}
                    onChange={handleInputChange}
                    required={isRequired("saleId")}
                  />
                </FormGroup>
              </Form>
            </Container>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EditDocumentModal;
